/**
 * ContactsList Component
 *
 * Displays a searchable, filterable list of contacts with pagination.
 * Click a contact to navigate to ContactDetailPage.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { contactsApi } from '@/services/contactsApi';
import type { ContactListItem } from '@/services/contactsApi';
import { useDebounce } from '@/hooks';
import { getInitials, getImportanceBadge } from './utils';

interface ContactsListProps {
  onCountChange?: (count: number) => void;
}

export default function ContactsList({ onCountChange }: ContactsListProps) {
  const { t } = useTranslation('people');
  const navigate = useNavigate();

  // State
  const [contacts, setContacts] = useState<ContactListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [importanceFilter, setImportanceFilter] = useState<string>('');

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (debouncedSearch.trim()) {
        // Semantic search mode
        const response = await contactsApi.searchSemantic(debouncedSearch, {
          limit: 50,
          mode: 'hybrid',
          includeCompanies: false,
        });
        // Transform semantic results to ContactListItem format
        const results: ContactListItem[] = response.results
          .filter(r => r.entity_type === 'contact')
          .map(r => ({
            id: r.id,
            first_name: r.full_name?.split(' ')[0] || '',
            last_name: r.full_name?.split(' ').slice(1).join(' ') || undefined,
            full_name: r.full_name || r.name || '',
            primary_email: r.primary_email || r.email || undefined,
            primary_phone: undefined,
            title: r.title || undefined,
            importance: r.importance || 'normal',
            avatar_url: r.avatar_url || undefined,
            last_contacted_at: undefined,
            companies: r.company
              ? [{
                  role_id: '',
                  company_id: '',
                  company_name: r.company,
                  title: r.title,
                  department: undefined,
                  role_type: 'employee',
                  is_current: true,
                  is_primary: true,
                }]
              : [],
          }));
        setContacts(results);
        setTotalPages(1);
        setTotalCount(results.length);
      } else {
        // List mode
        const response = await contactsApi.list(
          page,
          50,
          importanceFilter || undefined
        );
        setContacts(response.contacts);
        setTotalPages(response.total_pages);
        setTotalCount(response.total_count);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.failed_to_load', { type: 'contacts' }));
    } finally {
      setLoading(false);
    }
  }, [page, importanceFilter, debouncedSearch]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Notify parent of count changes
  useEffect(() => {
    if (onCountChange) {
      onCountChange(totalCount);
    }
  }, [totalCount, onCountChange]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, importanceFilter]);

  const handleContactClick = (contactId: string) => {
    navigate(`/people/contacts/${contactId}`);
  };

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => fetchContacts()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('contacts_list.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder={t('contacts_list.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <svg
            className="w-5 h-5 text-gray-400 absolute left-3 top-2.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <select
          value={importanceFilter}
          onChange={(e) => setImportanceFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">{t('contacts_list.filters.all_importance')}</option>
          <option value="vip">{t('contacts_list.filters.vip')}</option>
          <option value="high">{t('contacts_list.filters.high')}</option>
          <option value="normal">{t('contacts_list.filters.normal')}</option>
          <option value="low">{t('contacts_list.filters.low')}</option>
        </select>
      </div>

      {/* Contact list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <svg
            className="w-12 h-12 mx-auto text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p className="font-medium">{t('contacts_list.empty_state.title')}</p>
          <p className="text-sm mt-1">{t('contacts_list.empty_state.description')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('contacts_list.table_headers.name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('contacts_list.table_headers.email')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('contacts_list.table_headers.company')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('contacts_list.table_headers.importance')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('contacts_list.table_headers.last_contact')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {contacts.map((contact) => (
                <tr
                  key={contact.id}
                  onClick={() => handleContactClick(contact.id)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium">
                        {contact.avatar_url ? (
                          <img
                            src={contact.avatar_url}
                            alt=""
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(contact.full_name)
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {contact.full_name}
                        </div>
                        {contact.title && (
                          <div className="text-xs text-gray-500">{contact.title}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {contact.primary_email || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {contact.companies?.[0]?.company_name || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={getImportanceBadge(contact.importance)}>
                      {contact.importance}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {contact.last_contacted_at
                      ? new Date(contact.last_contacted_at).toLocaleDateString()
                      : t('contacts_list.last_contacted.never')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('contacts_list.pagination.previous')}
              </button>
              <span className="text-sm text-gray-500">
                {t('contacts_list.pagination.page_of', { current: page, total: totalPages })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('contacts_list.pagination.next')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
